import React from "react";
import {
	Typography,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Paper
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AuditChange, AuditLogEntry } from "../../models/auditLog";

interface ChangeDetailProps {
	auditLog: AuditLogEntry;
}

export const ChangeDetail: React.FC<ChangeDetailProps> = ({ auditLog }) => {
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === "dark";
	const changes = auditLog.changes ?? null;

	const renderValue = (value: unknown) => {
		if (value === null || value === undefined) {
			return <em>None</em>;
		}

		if (typeof value === "object") {
			return (
				<pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
					{JSON.stringify(value, null, 2)}
				</pre>
			);
		}

		return String(value);
	};

	const filterChangeObject = (
		obj: Record<string, unknown> | null | undefined
	): Record<string, unknown> | null | undefined => {
		if (!obj) return obj;
		const excludedKeys = ["description", "itemCode", "itemKey"];
		const filteredEntries = Object.entries(obj).filter(
			([key]) => !excludedKeys.includes(key)
		);
		return filteredEntries.length
			? Object.fromEntries(filteredEntries)
			: null;
	};

	const renderChangeCell = (value: unknown) => {
		if (
			(auditLog.entityType === "Invoice" ||
				auditLog.entityType === "Return") &&
			typeof value === "object" &&
			value !== null
		) {
			return renderObjectAsTable(
				filterChangeObject(value as Record<string, unknown>)
			);
		}

		return renderValue(value);
	};

	const renderMetaTable = (showItemCode = true) => (
		<Table size="small" sx={{ mb: 1 }}>
			<TableBody>
				{auditLog.metadata?.invoiceNumber && (
					<TableRow>
						<TableCell sx={{ fontWeight: 700 }}>
							Invoice #
						</TableCell>
						<TableCell>{auditLog.metadata.invoiceNumber}</TableCell>
					</TableRow>
				)}
				{showItemCode && auditLog.metadata?.itemCode && (
					<TableRow>
						<TableCell sx={{ fontWeight: 700 }}>
							Item Code
						</TableCell>
						<TableCell>{auditLog.metadata.itemCode}</TableCell>
					</TableRow>
				)}
				{auditLog.metadata?.itemName && (
					<TableRow>
						<TableCell sx={{ fontWeight: 700 }}>
							Item Name
						</TableCell>
						<TableCell>{auditLog.metadata.itemName}</TableCell>
					</TableRow>
				)}
			</TableBody>
		</Table>
	);

	const renderSoldItems = (items: any[], excludedKeys: string[] = []) => {
		if (!items || items.length === 0)
			return <Typography className="small">No items</Typography>;
		const cols = Object.keys(items[0]).filter(
			(key) => !excludedKeys.includes(key)
		);
		return (
			<Table size="small">
				<TableHead>
					<TableRow>
						{cols.map((c) => (
							<TableCell key={c}>{c}</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{items.map((it, idx) => (
						<TableRow key={idx}>
							{cols.map((c) => (
								<TableCell key={c}>
									{c === "quantity"
										? (it as any)[c] === null ||
											(it as any)[c] === undefined
											? "-"
											: String((it as any)[c])
										: renderValue((it as any)[c])}
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		);
	};

	const renderObjectAsTable = (
		obj: Record<string, unknown> | null | undefined
	) => {
		if (!obj) return <Typography className="small">-</Typography>;
		const keys = Object.keys(obj);
		return (
			<Table size="small">
				<TableBody>
					{keys.map((k) => (
						<TableRow key={k}>
							<TableCell sx={{ width: "40%" }}>{k}</TableCell>
							<TableCell>
								{renderValue((obj as any)[k])}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		);
	};

	const renderChangesTable = (changes: Record<string, AuditChange>) => (
		<Table size="small">
			<TableHead>
				<TableRow>
					<TableCell>Field</TableCell>
					<TableCell>Before</TableCell>
					<TableCell>After</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{Object.entries(changes).map(([field, diff]) => (
					<TableRow key={field}>
						<TableCell>{field}</TableCell>
						<TableCell>{renderChangeCell(diff.before)}</TableCell>
						<TableCell>{renderChangeCell(diff.after)}</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);

	const renderByEntity = () => {
		switch (auditLog.entityType) {
			case "Invoice":
				return (
					<>
						<Typography variant="subtitle1">Invoice</Typography>
						{renderMetaTable()}
						<Typography variant="subtitle2" sx={{ mt: 1 }}>
							Sold Items
						</Typography>
						{auditLog.metadata?.soldItems ? (
							renderSoldItems(
								auditLog.metadata.soldItems as any[],
								["itemKey"]
							)
						) : (
							<Typography className="small">
								No sold items
							</Typography>
						)}
						<Typography variant="subtitle2" sx={{ mt: 2 }}>
							Field Changes
						</Typography>
						{changes ? (
							renderChangesTable(changes)
						) : (
							<Typography className="small">
								No field-level changes recorded.
							</Typography>
						)}
					</>
				);
			case "Return":
				return (
					<>
						<Typography variant="subtitle1">Return</Typography>
						{renderMetaTable(false)}
						<Typography variant="subtitle2" sx={{ mt: 1 }}>
							Returned Items
						</Typography>
						{(auditLog.metadata as any)?.returnedItems &&
						(auditLog.metadata as any).returnedItems.length > 0 ? (
							renderSoldItems(
								(auditLog.metadata as any).returnedItems
							)
						) : (
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>itemCode</TableCell>
										<TableCell>quantity</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									<TableRow>
										<TableCell>
											{auditLog.metadata?.itemCode ?? "-"}
										</TableCell>
										<TableCell>{"-"}</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						)}
						<Typography variant="subtitle2" sx={{ mt: 2 }}>
							Field Changes
						</Typography>
						{changes ? (
							renderChangesTable(changes)
						) : (
							<Typography className="small">
								No field-level changes recorded.
							</Typography>
						)}
					</>
				);
			case "Item":
			default:
				return (
					<>
						<Typography variant="subtitle1">Item</Typography>
						{renderMetaTable()}
						<Typography variant="subtitle2" sx={{ mt: 1 }}>
							Field Changes
						</Typography>
						{changes ? (
							renderChangesTable(changes)
						) : (
							<Typography className="small">
								No field-level changes recorded.
							</Typography>
						)}
					</>
				);
		}
	};

	return (
		<Paper
			elevation={0}
			sx={{
				backgroundColor: isDarkMode
					? theme.palette.background.default
					: "#fafafa",
				p: 2,
				mb: 2
			}}
		>
			{renderByEntity()}
		</Paper>
	);
};
